import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, map, switchMap } from 'rxjs';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { CheckboxComponent } from '@components/checkbox';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { MsfSelect } from '@components/select/select';
import { MsfSelectPaginated } from '@components/select/paginated/select-paginated';
import { TextareaComponent } from '@components/textarea';
import { MsfInput } from '@components/ui/forms/input/input';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { ProcessParameterDefaultsService } from '@app/services/process-parameter-defaults.service';
import { AssignmentDefaultsState, buildAssignmentWithDefaults, prepareAssignmentDefaults } from '@app/pages/admin/assignment-start-defaults.util';
import { RegionDictionarySelectComponent } from '@app/pages/admin/components/region-dictionary-select/region-dictionary-select';
import {
  ASSIGNMENT_DATE_PLACEHOLDER_OPTIONS,
  ASSIGNMENT_DATE_SIGN_OPTIONS,
  ASSIGNMENT_DATE_UNIT_OPTIONS,
  AssignmentStartFormValue,
  AssignmentStartKind,
  GEOMETRY_SOURCE_OPTIONS,
  PRIORITY_OPTIONS,
  assignmentDateFieldState,
  assignmentDateFromMode,
  assignmentDateFromOffset,
  assignmentStartKind,
  canStartAssignment,
  defaultAssignmentStartForm,
  isSatelliteDownloadSelectionProcess,
} from '@app/pages/admin/assignment-start.util';
import { assignmentParameterHelp } from '@app/pages/admin/assignment-parameter-examples.util';
import { parameterFieldSchema } from '@app/pages/admin/assignment-parameter-fields.util';
import { DATE_PLACEHOLDER_EXAMPLES } from '@app/pages/admin/process-subscription-date.util';
import { orchestrationSatelliteProductUrl } from '@app/pages/admin/orchestration-identity.util';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { OrchestrationGeometryMapComponent } from '@app/pages/admin/components/orchestration-geometry-map/orchestration-geometry-map';
import { LOCALE_DEFAULT_END_ISO, LOCALE_DEFAULT_START_ISO } from '@app/utils/locale-date.util';

@Component({
  selector: 'app-assignment-start-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    CheckboxComponent,
    DatePickerComponent,
    MsfInput,
    MsfSelect,
    MsfSelectPaginated,
    RegionDictionarySelectComponent,
    TextareaComponent,
    CopyableIdentifierComponent,
    OrchestrationGeometryMapComponent,
    Loading,
    SecondaryButton,
  ],
  templateUrl: './assignment-start-form.html',
  styleUrl: './assignment-start-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentStartFormComponent {
  readonly processKey = input.required<string>();
  readonly seedJson = input<string | null>(null);
  readonly seedIsTemplate = input(false);
  /** Subscription dialog already has the name; the catalogue "Assignment name" field would repeat it. */
  readonly hideAssignmentName = input(false);
  readonly form = signal<AssignmentStartFormValue>(defaultAssignmentStartForm(''));
  readonly ready = signal(false);
  readonly defaultsLoading = signal(true);
  readonly defaultsError = signal('');
  readonly validationError = signal('');
  readonly hasInheritedDefaults = signal(false);
  private readonly defaultsApi = inject(ProcessParameterDefaultsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly defaultsRequests = new Subject<{ processKey: string; seed: string | null; template: boolean }>();
  private defaultsState: AssignmentDefaultsState | null = null;
  private readonly editedFields = new Set<string>();
  private jsonEdited = false;

  readonly kind = computed<AssignmentStartKind>(() => assignmentStartKind(this.processKey()));
  readonly satelliteProductAnalysisUrl = computed(() =>
    this.kind() === 'post-processing' ? orchestrationSatelliteProductUrl(this.form().satelliteProductId) : null,
  );
  readonly showCloudCover = computed(() => isSatelliteDownloadSelectionProcess(this.processKey()));
  readonly showCountryName = computed(() => this.form().geometrySource === 'CountryName');
  readonly showRegionIds = computed(() => this.form().geometrySource === 'Regions');
  readonly showGeoJson = computed(() => this.form().geometrySource === 'GeoJson');
  readonly showGeoJsonFile = computed(() => this.form().geometrySource === 'GeoJsonFile');
  readonly showFieldId = computed(() => this.processKey() === 'satellite.calculate-season-indices');
  readonly showForce = computed(() => this.processKey() === 'satellite.backfill-season-indices');
  readonly showDetectionJob = computed(() => this.processKey() === 'satellite.backfill-season-indices');

  readonly geometryOptions = GEOMETRY_SOURCE_OPTIONS;
  readonly priorityOptions = PRIORITY_OPTIONS;
  readonly datePlaceholderOptions = ASSIGNMENT_DATE_PLACEHOLDER_OPTIONS;
  readonly dateSignOptions = ASSIGNMENT_DATE_SIGN_OPTIONS;
  readonly dateUnitOptions = ASSIGNMENT_DATE_UNIT_OPTIONS;
  readonly datePlaceholderExamples = DATE_PLACEHOLDER_EXAMPLES;
  readonly parameterHelp = computed(() => assignmentParameterHelp(this.processKey()));
  readonly parameterFieldViews = computed(() => {
    const values = this.form().parameterFields ?? {};
    return (parameterFieldSchema(this.processKey()) ?? []).map(field => {
      const value = values[field.name] ?? '';
      const selected = new Set(value.split(',').map(item => item.trim()).filter(Boolean));
      const date = field.type === 'date' ? assignmentDateFieldState(value || null) : null;
      return {
        ...field,
        value,
        checked: value === 'true',
        regions: [...selected],
        options: (field.options ?? []).map(option => ({ ...option, selected: selected.has(option.id) })),
        date,
        calendar: date ? !date.showOffset : false,
      };
    });
  });
  readonly fieldService = inject(FieldService);
  readonly startDateField = computed(() => assignmentDateFieldState(this.form().startDate));
  readonly endDateField = computed(() => assignmentDateFieldState(this.form().endDate));
  readonly startDateIsCalendar = computed(() => !this.startDateField().showOffset);
  readonly endDateIsCalendar = computed(() => !this.endDateField().showOffset);

  constructor() {
    this.defaultsRequests.pipe(
      switchMap(request => {
        this.defaultsLoading.set(true);
        this.ready.set(false);
        this.defaultsError.set('');
        return this.defaultsApi.load(request.processKey).pipe(
          map(response => ({ request, state: prepareAssignmentDefaults(request.processKey, response.parameters, request.seed, request.template) })),
          catchError(() => {
            this.defaultsLoading.set(false);
            this.defaultsError.set('PROCESS_DEFAULTS_LOAD_FAILED');
            return EMPTY;
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ request, state }) => {
      if (request.processKey !== this.processKey() || request.seed !== this.seedJson() || request.template !== this.seedIsTemplate()) return;
      this.defaultsState = state;
      this.hasInheritedDefaults.set(Object.keys(state.inherited).length > 0);
      this.editedFields.clear();
      this.jsonEdited = false;
      this.form.set(structuredClone(state.value));
      this.defaultsLoading.set(false);
      this.updateReady();
    });
    effect(() => {
      const processKey = this.processKey();
      const seedJson = this.seedJson();
      const template = this.seedIsTemplate();
      untracked(() => this.resetFor(processKey, seedJson, template));
    });
  }

  reset(): void {
    this.resetFor(this.processKey(), this.seedJson(), this.seedIsTemplate());
  }

  build() {
    if (this.defaultsLoading() || !this.defaultsState || this.defaultsError()) {
      return { ok: false as const, error: this.defaultsError() || 'PROCESS_DEFAULTS_LOADING' };
    }
    return buildAssignmentWithDefaults(this.processKey(), this.defaultsState, this.form(), this.editedFields, this.jsonEdited);
  }

  setName(value: string): void {
    this.patch({ name: value });
  }

  setGeometrySource(value: string): void {
    this.patch({ geometrySource: value ?? 'CountryBoundaries' });
  }

  setRegionIds(value: string[]): void {
    this.patch({ regionIds: value });
  }

  setCountryName(value: string): void {
    this.patch({ countryName: value });
  }

  setGeoJson(value: string): void {
    this.patch({ geoJson: value });
  }

  setGeoJsonFromMap(value: string): void {
    this.patch({ geometrySource: 'GeoJson', geoJson: value });
  }

  setGeoJsonFileId(value: string): void {
    this.patch({ geoJsonFileId: value });
  }

  setStartDateMode(value: string): void {
    this.patch({
      startDate: assignmentDateFromMode(value, LOCALE_DEFAULT_START_ISO, this.form().startDate),
    });
  }

  setStartDate(value: Date | string | null): void {
    this.patch({ startDate: value });
  }

  setStartDateSign(value: string): void {
    this.patch({
      startDate: assignmentDateFromOffset(this.form().startDate, { sign: value === '+' ? '+' : '-' }),
    });
  }

  setStartDateAmount(value: unknown): void {
    this.patch({
      startDate: assignmentDateFromOffset(this.form().startDate, { amount: value as number }),
    });
  }

  setStartDateUnit(value: string): void {
    this.patch({
      startDate: assignmentDateFromOffset(this.form().startDate, {
        unit: value === 'h' || value === 'm' ? value : 'd',
      }),
    });
  }

  setEndDateMode(value: string): void {
    this.patch({
      endDate: assignmentDateFromMode(value, LOCALE_DEFAULT_END_ISO, this.form().endDate),
    });
  }

  setEndDate(value: Date | string | null): void {
    this.patch({ endDate: value });
  }

  setEndDateSign(value: string): void {
    this.patch({
      endDate: assignmentDateFromOffset(this.form().endDate, { sign: value === '+' ? '+' : '-' }),
    });
  }

  setEndDateAmount(value: unknown): void {
    this.patch({
      endDate: assignmentDateFromOffset(this.form().endDate, { amount: value as number }),
    });
  }

  setEndDateUnit(value: string): void {
    this.patch({
      endDate: assignmentDateFromOffset(this.form().endDate, {
        unit: value === 'h' || value === 'm' ? value : 'd',
      }),
    });
  }

  setSentinel(value: boolean): void {
    this.patch({ sentinel: value });
  }

  setLandsat(value: boolean): void {
    this.patch({ landsat: value });
  }

  setPriority(value: string): void {
    this.patch({ priority: value ?? 'Normal' });
  }

  setCloudCover(value: unknown): void {
    if (value === '' || value === null || value === undefined) {
      this.patch({ maximumCloudCover: null });
      return;
    }
    const parsed = Number(value);
    this.patch({ maximumCloudCover: Number.isFinite(parsed) ? parsed : null });
  }

  setSatelliteProductId(value: string): void {
    this.patch({ satelliteProductId: value });
  }

  setMaxReportsPerRun(value: unknown): void {
    const parsed = Number(value);
    this.patch({ maxReportsPerRun: Number.isFinite(parsed) ? parsed : 0 });
  }

  setAlsoPdf(value: boolean): void {
    this.patch({ alsoPdf: !!value });
  }

  setLocale(value: string): void {
    this.patch({ locale: value });
  }

  setBucket(value: string): void {
    this.patch({ bucket: value });
  }

  setCatchUpTake(value: unknown): void {
    const parsed = Number(value);
    this.patch({ catchUpTake: Number.isFinite(parsed) ? parsed : 0 });
  }

  setFailEvery(value: unknown): void {
    const parsed = Number(value);
    this.patch({ failEvery: Number.isFinite(parsed) ? parsed : 0 });
  }

  setFieldId(value: string | null | undefined): void {
    this.patch({ fieldId: value ?? '' });
  }

  setForceSeasonIndices(value: boolean): void {
    this.patch({ forceSeasonIndices: !!value });
  }

  setPolygonDetectionJobId(value: string): void {
    this.patch({ polygonDetectionJobId: value ?? '' });
  }

  setParametersJson(value: string): void {
    this.jsonEdited = true;
    this.patch({ parametersJson: value });
  }

  setParameterField(name: string, value: unknown): void {
    this.patchParameter(name, value == null ? '' : String(value));
  }

  setParameterChecked(name: string, value: boolean): void {
    this.patchParameter(name, value ? 'true' : 'false');
  }

  setParameterRegions(name: string, ids: string[]): void {
    this.patchParameter(name, (ids ?? []).filter(Boolean).join(','));
  }

  setParameterCheck(name: string, optionId: string, selected: boolean): void {
    const current = new Set((this.form().parameterFields?.[name] ?? '').split(',').filter(Boolean));
    if (selected) {
      current.add(optionId);
    } else {
      current.delete(optionId);
    }
    this.patchParameter(name, [...current].join(','));
  }

  setParameterDateMode(name: string, mode: string): void {
    const next = assignmentDateFromMode(mode, undefined, this.form().parameterFields?.[name] || null);
    this.setParameterDate(name, next);
  }

  setParameterDateSign(name: string, sign: string): void {
    this.patchParameter(name, assignmentDateFromOffset(this.form().parameterFields?.[name] || null, {
      sign: sign === '+' ? '+' : '-',
    }));
  }

  setParameterDateAmount(name: string, amount: unknown): void {
    this.patchParameter(name, assignmentDateFromOffset(this.form().parameterFields?.[name] || null, {
      amount: amount as number,
    }));
  }

  setParameterDateUnit(name: string, unit: string): void {
    this.patchParameter(name, assignmentDateFromOffset(this.form().parameterFields?.[name] || null, {
      unit: unit === 'h' || unit === 'm' ? unit : 'd',
    }));
  }

  setParameterDate(name: string, value: Date | string | null): void {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      this.patchParameter(name, `${value.getFullYear()}-${month}-${day}`);
      return;
    }
    this.patchParameter(name, typeof value === 'string' ? value : '');
  }

  private patchParameter(name: string, value: string): void {
    this.editedFields.add(name);
    this.patch({ parameterFields: { ...this.form().parameterFields, [name]: value } });
  }

  private resetFor(processKey: string, seedJson?: string | null, template = false): void {
    if (processKey) this.defaultsRequests.next({ processKey, seed: seedJson ?? null, template });
  }

  private patch(partial: Partial<AssignmentStartFormValue>): void {
    if (this.defaultsLoading()) return;
    const value = { ...this.form(), ...partial };
    this.form.set(value);
    this.updateReady();
  }

  private updateReady(): void {
    const built = this.build();
    this.validationError.set(built.ok ? '' : built.error);
    this.ready.set(!this.defaultsLoading() && canStartAssignment(this.processKey(), this.form()) && built.ok);
  }
}
